import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Label,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { MutateRouteFormValues } from './routeForm';
import { ShipSelector } from '../../utils/ShipCommand';
import { ShapeSelect } from '../../Shapes/ShapeSelects';
import { useLocalization } from '@ais-ui/locale-management';
import { useRoutes } from '../RoutesContext';
import { Port } from '@ais-ui/api-spec/portserver';
import { PiAnchor } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { useSelectionState } from '../../../components/SelectionStateProvider';

interface MutateRouteFormProps {
  form: UseFormReturn<MutateRouteFormValues>;
}

export const MutateRouteForm = ({ form }: MutateRouteFormProps) => {
  const { messages } = useLocalization();
  const { currentPort } = useSelectionState();
  const { shape, destinationType } = useRoutes();

  useEffect(() => {
    if (destinationType === 'port' && currentPort) {
      form.setValue('destinationPortId', currentPort.id);
    } else if (destinationType === 'shape' && shape?.properties?.id) {
      form.setValue('destinationShapeId', shape.properties.id);
    }
  }, [destinationType, currentPort, shape, form]);

  const portSelect: Port | null = useMemo(() => {
    if (!currentPort) {
      return null;
    }
    return {
      id: Number(currentPort.id),
      locode: currentPort.locode,
      name: currentPort.name,
      country: currentPort.country,
    };
  }, [currentPort]);

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="sourceShip"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{messages.routesSaveDialogShipSourceLabel} *</FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <ShipSelector
                value={field.value ?? null}
                onChange={field.onChange}
              ></ShipSelector>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {destinationType === 'port' && portSelect && (
        <div className="flex space-x-4 items-center">
          <div className="flex border space-x-4 rounded-lg px-4 py-2 w-full">
            <Label className="text-base mb-1">Destination </Label>
            <div className="text-muted-foreground">
              <div className="flex space-x-2 items-center">
                <PiAnchor></PiAnchor>
                <p>{portSelect.name}</p>
                <div className={cn('rounded-full h-1 w-1 bg-blue-600')}></div>
                <p>{portSelect.country}</p>
                <div className={cn('rounded-full h-1 w-1 bg-blue-600')}></div>
                <p>{portSelect.locode}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {destinationType === 'shape' && (
        <div className="space-y-2">
          <FormField
            control={form.control}
            name="destinationShapeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="">
                  {messages.routesSaveDialogDestinationLabel} *
                </FormLabel>
                <FormControl onChange={() => form.clearErrors()}>
                  <ShapeSelect
                    id="route-location-select"
                    value={field.value}
                    onChange={field.onChange}
                    allowedGeometryTypes={['Polygon', 'Point']}
                  ></ShapeSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};
